import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../ui/Button';
import Logo from '../ui/Logo';
import { Menu01Icon, Cancel01Icon, Calendar03Icon, Search01Icon, Globe02Icon } from 'hugeicons-react';
import { useLandingPageSettingsQuery } from '../../lib/queries';

export default function Header({ onOpenBooking }: { onOpenBooking: () => void }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Scrolled state for styling
      setIsScrolled(currentScrollY > 20);

      // Visibility logic: hide when scrolling down, show when scrolling up
      // Only hide on mobile, keep visible on desktop
      const isDesktop = window.innerWidth >= 768;
      if (!isDesktop && currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const navigate = useNavigate();

  const { data: landingSettings } = useLandingPageSettingsQuery();
  const showWorks = landingSettings?.show_works ?? true;

  const navLinks = useMemo(() => {
    const links = [
      { name: 'الرئيسية', href: '/' },
      { name: 'من نحن', href: '#about-us' },
      { 
        name: 'أعمالنا', 
        href: '/our-works',
        subLinks: [
          { name: 'معرض الصور', href: '/our-works' },
          { name: 'قبل وبعد', href: '/our-works' },
        ]
      },
      { 
        name: 'الخدمات', 
        href: '/services',
        subLinks: [
          { name: 'العناية الشاملة', href: '/services' },
          { name: 'النانو سيراميك', href: '/services' },
          { name: 'أفلام حماية PPF', href: '/services' },
          { name: 'تلميع وبولش', href: '/services' },
          { name: 'تنظيف داخلي عميق', href: '/services' },
        ]
      },
      { name: 'كيف نعمل', href: '#how-it-works' },
      { 
        name: 'موقعنا', 
        href: '#location',
        subLinks: [
          { name: 'الاتجاهات في الموقع', href: '#location' },
          { name: 'افتح في خرائط جوجل', href: 'https://maps.app.goo.gl/uX3L5q6mN6p6uCZb9', external: true },
        ]
      },
    ];

    // Remove "أعمالنا" when show_works is false
    if (!showWorks) {
      return links.filter((l) => l.href !== '/our-works');
    }
    return links;
  }, [showWorks]);

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    
    // Handle external routes (not hash links)
    if (href.startsWith('http')) {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }

    // Handle Home click when already on home page
    if (href === '/' && location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!href.startsWith('#')) {
      navigate(href);
      return;
    }
    
    if (location.pathname !== '/' && href.startsWith('#')) {
        navigate('/' + href);
        return;
    }

    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  if (location.pathname !== '/' && location.pathname !== '/privacy-policy' && location.pathname !== '/terms' && location.pathname !== '/our-works' && location.pathname !== '/services' && !location.pathname.startsWith('/track')) return null;

  return (
    <>
      <motion.header
        className={`fixed left-0 right-0 z-50 transition-all duration-500 ease-in-out ${
            isScrolled ? 'top-6' : 'top-0'
        }`}
        initial={{ y: -100 }}
        animate={{ y: isVisible ? 0 : -120 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className={`container mx-auto px-3 sm:px-4 transition-all duration-500 ${isScrolled ? 'max-w-7xl' : 'max-w-full'}`}>
          <div className={`
            flex items-center justify-between
            transition-all duration-500 ease-in-out border
            ${isScrolled 
                ? 'bg-[#050505]/80 backdrop-blur-xl rounded-2xl border-white/[0.08] px-3 sm:px-6 py-2 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.5)]' 
                : 'bg-transparent border-transparent px-3 sm:px-8 py-4 sm:py-6'
            }
          `}>
            
            {/* Logo */}
            <Link 
              to="/" 
              onClick={(e) => {
                if (location.pathname === '/') {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              className="flex items-center gap-1.5 group"
            >
              <Logo className={`h-auto group-hover:scale-105 transition-all duration-300 ${isScrolled ? 'w-16 md:w-20' : 'w-20 md:w-28'}`} />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
              {navLinks.map((link) => (
                <div key={link.name} className="relative group/nav">
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick(link.href);
                    }}
                    className="text-sm font-medium text-gray-300 hover:text-white transition-colors relative flex items-center gap-1 py-4"
                  >
                    {link.name}
                    <span className="absolute bottom-3 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-primary transition-all group-hover/nav:w-full" />
                  </a>

                  {/* Dropdown */}
                  {link.subLinks && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 translate-y-2 pointer-events-none group-hover/nav:opacity-100 group-hover/nav:translate-y-0 group-hover/nav:pointer-events-auto transition-all duration-300 z-50">
                      <div className="bg-[#0a0a0a] border border-white/[0.08] rounded-xl overflow-hidden min-w-[200px] shadow-2xl backdrop-blur-xl">
                        <div className="p-2 flex flex-col">
                          {link.subLinks.map((sub) => (
                            sub.external ? (
                              <a
                                key={sub.name}
                                href={sub.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-4 py-2.5 text-xs font-bold text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-all"
                              >
                                {sub.name}
                              </a>
                            ) : (
                              <Link
                                key={sub.name}
                                to={sub.href}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="px-4 py-2.5 text-xs font-bold text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-all"
                              >
                                {sub.name}
                              </Link>
                            )
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Actions */}
            <div className="hidden md:flex items-center gap-4">
               <button className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-full transition-colors">
                  <Globe02Icon size={20} />
               </button>

               <Link 
                 to="/track" 
                 className="flex items-center gap-2 text-sm font-bold text-gray-300 hover:text-white transition-colors px-3 py-2 rounded-lg hover:bg-white/5"
               >
                 <Search01Icon size={18} />
                 <span>متابعة طلب</span>
               </Link>

               <Button onClick={() => onOpenBooking()} size="sm" className="shadow-lg shadow-primary/20">
                 <span>احجز موعدك</span>
                 <Calendar03Icon size={18} />
               </Button>
            </div>

            {/* Mobile Toggle */}
            <button 
                className="md:hidden p-2 text-white bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(true)}
            >
                <Menu01Icon size={24} />
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-xl flex items-center justify-center p-4"
          >
             <div className="w-full max-w-md">
                <div className="flex justify-between items-center mb-12">
                   <Link to="/" className="flex items-center gap-1.5" onClick={() => setIsMobileMenuOpen(false)}>
                      <Logo className="w-32 h-auto" />
                   </Link>
                   
                   <button 
                        className="p-3 text-white bg-white/10 hover:bg-white/20 rounded-full backdrop-blur-md transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                    >
                        <Cancel01Icon size={24} />
                    </button>
                </div>

                <nav className="flex flex-col gap-6">
                    {navLinks.map((link, i) => (
                        <motion.a
                            key={link.name}
                            href={link.href}
                            onClick={(e) => {
                                e.preventDefault();
                                handleNavClick(link.href);
                            }}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.1 }}
                            className="text-2xl font-bold text-white hover:text-primary transition-colors flex items-center justify-between group"
                        >
                            <span>{link.name}</span>
                            <span className="w-2 h-2 rounded-full bg-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                        </motion.a>
                    ))}
                </nav>

                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mt-12 flex flex-col gap-4"
                >
                    <Link 
                        to="/track"
                        className="w-full py-4 rounded-xl border border-white/10 text-white font-bold text-center hover:bg-white/5 transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                    >
                        متابعة حالة الطلب
                    </Link>
                    
                    <Button 
                        onClick={() => {
                            setIsMobileMenuOpen(false);
                            onOpenBooking();
                        }} 
                        className="w-full justify-center py-4 text-lg"
                    >
                        حجز موعد جديد
                    </Button>
                </motion.div>
             </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
